import React from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { GridColDef } from '@mui/x-data-grid';

import { FDataGrid } from '../grid';

import {
  ISensitivityResultRow,
  ISensitivitySkippedRow,
  ISensitivityWorkbench,
} from './simulation.models';


type Props = {
  workbench: ISensitivityWorkbench | null;
  loading?: boolean;
  editable?: boolean;
  canRun?: boolean;

  onRun: (
    targetdrivercode: string,
    deltapercent: number,
  ) => void | Promise<void>;

  onRefresh: () => void | Promise<void>;
};


type State = {
  targetdrivercode: string;
  deltapercent: number;

  showSkipped: boolean;
  viewMode: 'GRID' | 'TORNADO';
};


class SimulationSensitivityPanel extends React.Component<Props, State> {

  state: State = {
    targetdrivercode: 'RESPARTVAL',
    deltapercent: 5,

    showSkipped: false,
    viewMode: 'GRID',
  };


  componentDidUpdate(prevProps: Props) {
    const previousRun = prevProps.workbench?.run;
    const currentRun = this.props.workbench?.run;

    if (
      currentRun &&
      (
        previousRun?.runid !== currentRun.runid ||
        previousRun?.deltapercent !== currentRun.deltapercent
      )
    ) {
      this.setState({
        targetdrivercode:
          currentRun.targetdrivercode || 'RESPARTVAL',

        deltapercent:
          Number(currentRun.deltapercent ?? 5),
      });
    }
  }


  private formatNumber = (
    value?: number | null,
    maximumFractionDigits = 2,
  ): string => {

    if (value === null || value === undefined) {
      return '—';
    }

    return Number(value).toLocaleString(undefined, {
      maximumFractionDigits,
    });
  };


  private formatCompact = (
    value?: number | null,
  ): string => {

    if (value === null || value === undefined) {
      return '—';
    }

    const n = Number(value);

    if (Math.abs(n) >= 1_000_000_000) {
      return `${(n / 1_000_000_000).toFixed(2)} B`;
    }

    if (Math.abs(n) >= 1_000_000) {
      return `${(n / 1_000_000).toFixed(2)} M`;
    }

    if (Math.abs(n) >= 1_000) {
      return `${(n / 1_000).toFixed(2)} K`;
    }

    return n.toLocaleString(undefined, {
      maximumFractionDigits: 2,
    });
  };


  private influenceChip = (value?: string) => {
    const code = String(value || '').toUpperCase();

    switch (code) {

      case 'HIGH':
        return (
          <Chip
            size="small"
            label="HIGH"
            color="error"
            variant="outlined"
          />
        );

      case 'MEDIUM':
        return (
          <Chip
            size="small"
            label="MEDIUM"
            color="warning"
            variant="outlined"
          />
        );

      case 'NO_MEASURED_EFFECT':
        return (
          <Chip
            size="small"
            label="NO EFFECT"
            variant="outlined"
          />
        );

      default:
        return (
          <Chip
            size="small"
            label={code || 'LOW'}
            color="success"
            variant="outlined"
          />
        );
    }
  };


  private columns: GridColDef<ISensitivityResultRow>[] = [

    {
      field: 'rank',
      headerName: '#',
      width: 60,
      type: 'number',
    },

    {
      field: 'drivercode',
      headerName: 'Driver',
      width: 145,
    },

    {
      field: 'drivername',
      headerName: 'Name',
      minWidth: 200,
      flex: 1,
    },

    {
      field: 'influencesharepercent',
      headerName: 'Influence Share',
      width: 140,

      valueFormatter: (value: any) =>
        value == null
          ? '—'
          : `${Number(value).toFixed(2)}%`,
    },

    {
      field: 'downsideimpact',
      headerName: 'Downside Impact',
      width: 160,

      valueFormatter: (value: any) =>
        this.formatCompact(value),
    },

    {
      field: 'upsideimpact',
      headerName: 'Upside Impact',
      width: 160,

      valueFormatter: (value: any) =>
        this.formatCompact(value),
    },

    {
      field: 'downsideelasticity',
      headerName: 'Elasticity ↓',
      width: 125,

      valueFormatter: (value: any) =>
        value == null
          ? '—'
          : Number(value).toFixed(3),
    },

    {
      field: 'upsideelasticity',
      headerName: 'Elasticity ↑',
      width: 125,

      valueFormatter: (value: any) =>
        value == null
          ? '—'
          : Number(value).toFixed(3),
    },

    {
      field: 'influenceclass',
      headerName: 'Influence',
      width: 135,

      renderCell: (params: any) =>
        this.influenceChip(params.value),
    },
  ];


  private skippedColumns: GridColDef<ISensitivitySkippedRow>[] = [

    {
      field: 'drivercode',
      headerName: 'Driver',
      width: 150,
    },

    {
      field: 'drivername',
      headerName: 'Name',
      minWidth: 220,
      flex: 1,
    },

    {
      field: 'flowcode',
      headerName: 'Flow',
      width: 140,
    },

    {
      field: 'unit',
      headerName: 'Unit',
      width: 110,
    },

    {
      field: 'baselinevalue',
      headerName: 'Baseline',
      width: 145,

      valueFormatter: (value: any) =>
        this.formatNumber(value),
    },

    {
      field: 'skipreason',
      headerName: 'Reason Not Evaluated',
      minWidth: 280,
      flex: 1,
    },
  ];


  private renderTornado = (
    rows: ISensitivityResultRow[],
  ) => {
    const ranked = [...rows]
      .sort((a, b) => {
        const ai = Math.max(
          Math.abs(Number(a.downsideimpact ?? 0)),
          Math.abs(Number(a.upsideimpact ?? 0)),
          Math.abs(Number(a.absoluteimpact ?? 0)),
        );

        const bi = Math.max(
          Math.abs(Number(b.downsideimpact ?? 0)),
          Math.abs(Number(b.upsideimpact ?? 0)),
          Math.abs(Number(b.absoluteimpact ?? 0)),
        );

        return bi - ai;
      });

    const maximumImpact = ranked.reduce(
      (maximum, row) => Math.max(
        maximum,
        Math.abs(Number(row.downsideimpact ?? 0)),
        Math.abs(Number(row.upsideimpact ?? 0)),
      ),
      0,
    );

    const scale = (value?: number | null) => {
      if (!maximumImpact) {
        return 0;
      }

      return Math.min(
        100,
        (Math.abs(Number(value ?? 0)) / maximumImpact) * 100,
      );
    };

    return (
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800 }}
              >
                Sensitivity Tornado
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Drivers ranked by measured impact on the selected target.
                Left and right bars represent the downside and upside experiments
                around the current Simulation baseline.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '120px minmax(0, 1fr)',
                  md: '220px minmax(0, 1fr)',
                },
                columnGap: 2,
                rowGap: 1.5,
                alignItems: 'center',
              }}
            >
              <Box />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1px 1fr',
                  alignItems: 'center',
                  minHeight: 28,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textAlign: 'right', pr: 1 }}
                >
                  Downside
                </Typography>

                <Box
                  sx={{
                    width: '1px',
                    alignSelf: 'stretch',
                    bgcolor: 'divider',
                  }}
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pl: 1 }}
                >
                  Upside
                </Typography>
              </Box>

              {ranked.map((row) => {
                const downside = Number(row.downsideimpact ?? 0);
                const upside = Number(row.upsideimpact ?? 0);
                const downsideWidth = scale(downside);
                const upsideWidth = scale(upside);

                return (
                  <React.Fragment
                    key={row.id ?? row.drivercode}
                  >
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="caption" display="block">
                            {row.drivername || row.drivercode}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Baseline driver: {this.formatNumber(row.baselinevalue)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Low test: {this.formatNumber(row.lowvalue)} → {this.formatCompact(row.lowtargetvalue)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            High test: {this.formatNumber(row.highvalue)} → {this.formatCompact(row.hightargetvalue)}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700 }}
                          noWrap
                        >
                          {row.drivername || row.drivercode}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {row.drivercode}
                          {row.influencesharepercent != null
                            ? ` · ${Number(row.influencesharepercent).toFixed(1)}%`
                            : ''}
                        </Typography>
                      </Box>
                    </Tooltip>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1px 1fr',
                        alignItems: 'stretch',
                        minHeight: 36,
                      }}
                    >
                      <Tooltip
                        title={`Downside impact: ${this.formatCompact(downside)}`}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            pr: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: `${downsideWidth}%`,
                              minWidth: downsideWidth > 0 ? 2 : 0,
                              height: 22,
                              bgcolor: 'error.main',
                              opacity: 0.78,
                              borderRadius: '4px 0 0 4px',
                            }}
                          />
                        </Box>
                      </Tooltip>

                      <Box sx={{ bgcolor: 'text.secondary' }} />

                      <Tooltip
                        title={`Upside impact: ${this.formatCompact(upside)}`}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            pl: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: `${upsideWidth}%`,
                              minWidth: upsideWidth > 0 ? 2 : 0,
                              height: 22,
                              bgcolor: 'success.main',
                              opacity: 0.78,
                              borderRadius: '0 4px 4px 0',
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>

            <Divider />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
            >
              <Typography variant="caption" color="text.secondary">
                Scale is normalized to the largest measured absolute impact in this run.
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Baseline target: {this.formatCompact(this.props.workbench?.run?.baselinevalue)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };


  render() {

    const w = this.props.workbench;

    const run = w?.run;
    const summary = w?.summary;

    const rows = w?.records || [];
    const skipped = w?.skipped || [];

    const hasResults =
      Boolean(summary?.hasresults) &&
      rows.length > 0;


    return (
      <Stack spacing={2}>

        {this.props.loading && (
          <Box>
            <LinearProgress />

            <Typography
              variant="caption"
              color="text.secondary"
            >
              Running sensitivity analysis…
            </Typography>
          </Box>
        )}


        <Card elevation={1}>
          <CardContent>

            <Stack spacing={2}>

              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800 }}
                >
                  Measured Sensitivity Analysis
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  One-at-a-time local sensitivity using isolated
                  analytical scenarios and full Simulation
                  recomputation.
                </Typography>
              </Box>


              <Grid container spacing={2}>

                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    label="Target"
                    value="Partial Result (RESPARTVAL)"
                    disabled
                  />
                </Grid>


                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Delta %"

                    value={this.state.deltapercent}

                    inputProps={{
                      min: 0.1,
                      max: 100,
                      step: 0.5,
                    }}

                    onChange={(event) =>
                      this.setState({
                        deltapercent:
                          Number(event.target.value || 5),
                      })
                    }

                    disabled={this.props.loading || !(this.props.editable)}
                    
                  />
                </Grid>


                <Grid size={{ xs: 12, md: 4 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ScienceIcon />}

                    sx={{ height: '100%' }}

                    disabled={
                      this.props.loading ||
                      !this.props.editable ||
                      !this.props.canRun ||
                      this.state.deltapercent <= 0
                    }

                    onClick={() =>
                      this.props.onRun(
                        this.state.targetdrivercode,
                        this.state.deltapercent,
                      )
                    }
                  >
                    Run Sensitivity
                  </Button>
                </Grid>

              </Grid>

            </Stack>

          </CardContent>
        </Card>


        {run && (
          <Card elevation={1}>
            <CardContent>

              <Grid container spacing={2}>

                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Target
                  </Typography>

                  <Typography sx={{ fontWeight: 700 }}>
                    {run.targetdrivername || run.targetdrivercode}
                  </Typography>
                </Grid>


                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Delta
                  </Typography>

                  <Typography sx={{ fontWeight: 700 }}>
                    ±{run.deltapercent}%
                  </Typography>
                </Grid>


                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Baseline Result
                  </Typography>

                  <Typography sx={{ fontWeight: 700 }}>
                    {this.formatCompact(run.baselinevalue)}
                  </Typography>
                </Grid>


                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Drivers Tested
                  </Typography>

                  <Typography sx={{ fontWeight: 700 }}>
                    {summary?.evaluatedcount ?? 0}
                  </Typography>
                </Grid>


                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Top Influence
                  </Typography>

                  <Typography sx={{ fontWeight: 700 }}>
                    {summary?.topdrivercode || '—'}
                  </Typography>
                </Grid>

              </Grid>

            </CardContent>
          </Card>
        )}


        {!run && !this.props.loading && (
          <Alert severity="info">
            No completed Sensitivity Analysis exists for this
            Simulation Scenario. Run a sensitivity analysis to
            generate the measured ranking.
          </Alert>
        )}


        {hasResults && (
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                value={this.state.viewMode}
                onChange={(_event, value) => {
                  if (value) {
                    this.setState({ viewMode: value });
                  }
                }}
                aria-label="Sensitivity result view"
              >
                <ToggleButton value="GRID">
                  Grid
                </ToggleButton>

                <ToggleButton value="TORNADO">
                  Tornado
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {this.state.viewMode === 'GRID' ? (
              <FDataGrid
                title="Measured Driver Sensitivity"
                subtitle="Ranked by actual absolute impact on the selected target."

                rows={rows}
                columns={this.columns}

                loading={this.props.loading}

                height={520}

                showQuickFilter

                refresh={this.props.onRefresh}

                getRowId={(row: ISensitivityResultRow) =>
                  row.id ??
                  `${run?.runid || 'SEN'}-${row.drivercode}`
                }
              />
            ) : (
              this.renderTornado(rows)
            )}
          </Stack>
        )}


        {skipped.length > 0 && (
          <Card elevation={1}>
            <CardContent>

              <Stack spacing={1}>

                <Button
                  variant="text"

                  endIcon={
                    this.state.showSkipped
                      ? <ExpandLessIcon />
                      : <ExpandMoreIcon />
                  }

                  onClick={() =>
                    this.setState({
                      showSkipped:
                        !this.state.showSkipped,
                    })
                  }
                >
                  Not evaluated ({skipped.length})
                </Button>


                <Collapse in={this.state.showSkipped}>
                  <Divider sx={{ mb: 2 }} />

                  <FDataGrid
                    title="Drivers Not Evaluated"
                    subtitle="Drivers excluded because a scientifically meaningful local perturbation was unavailable."

                    rows={skipped}
                    columns={this.skippedColumns}

                    height={320}

                    getRowId={(row: ISensitivitySkippedRow) =>
                      row.id ?? row.drivercode
                    }
                  />
                </Collapse>

              </Stack>

            </CardContent>
          </Card>
        )}

      </Stack>
    );
  }
}


export default SimulationSensitivityPanel;